import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@/shared/utils";
import { useGetRegionsQuery, useGetCitiesByRegionQuery } from "@/shared/api/common";
import type { RegionResponse } from "@/shared/api/common";
import Select from "../Select";

interface Props {
  regionLabel?: string;
  regionPlaceholder?: string;
  cityLabel?: string;
  cityPlaceholder?: string;
  regionError?: string;
  cityError?: string;
  hasRequiredLabel?: boolean;
  value?: number;
  onChange?: (cityId: number | undefined) => void;
  regionId?: number;
  onRegionChange?: (regionId: number | undefined, cityType: "region" | "national_city") => void;
  disabled?: boolean;
}

function getRegionName(region: RegionResponse, locale: Locale): string {
  if (locale === "kk") return region.name_kk;
  if (locale === "en") return region.name_en;
  return region.name_ru;
}

export default function RegionCitySelect({
  regionLabel,
  regionPlaceholder,
  cityLabel,
  cityPlaceholder,
  regionError,
  cityError,
  hasRequiredLabel = false,
  value,
  onChange,
  regionId,
  onRegionChange,
  disabled = false,
}: Props) {
  const { t, i18n } = useTranslation("Common");
  const locale = i18n.language as Locale;

  const { data: regions = [], isLoading: isLoadingRegions } = useGetRegionsQuery();

  const selectedRegion = useMemo(
    () => regions.find((r) => r.id === regionId),
    [regions, regionId],
  );

  const shouldShowCitySelect = selectedRegion?.city_type === "region";

  const { data: citiesByRegion = [], isLoading: isLoadingCities } = useGetCitiesByRegionQuery(
    regionId!,
    { skip: !shouldShowCitySelect || typeof regionId !== "number" },
  );

  const regionOptions = useMemo(
    () => regions.map((r) => ({ label: getRegionName(r, locale), value: r.id })),
    [regions, locale],
  );

  const cityOptions = useMemo(
    () => citiesByRegion.map((c) => ({
      label: locale === "kk" ? c.name_kk : locale === "en" ? c.name_en : c.name_ru,
      value: c.id,
    })),
    [citiesByRegion, locale],
  );

  function handleRegionChange(selectedValue: number | null) {
    if (selectedValue === null) {
      onRegionChange?.(undefined, "region");
      onChange?.(undefined);
      return;
    }

    const region = regions.find((r) => r.id === selectedValue);
    if (!region) return;

    onRegionChange?.(region.id, region.city_type);
  }

  function handleCityChange(selectedValue: number | null) {
    onChange?.(selectedValue ?? undefined);
  }

  return (
    <div className="flex flex-col gap-4">
      <Select<number>
        label={regionLabel}
        hasRequiredLabel={hasRequiredLabel}
        placeholder={isLoadingRegions ? t("loadingRegions") : regionPlaceholder}
        options={regionOptions}
        value={regionId ?? null}
        onChange={handleRegionChange}
        error={regionError}
        disabled={disabled || isLoadingRegions}
      />

      {shouldShowCitySelect && (
        <Select<number>
          label={cityLabel}
          hasRequiredLabel={hasRequiredLabel}
          placeholder={isLoadingCities ? t("loadingCities") : cityPlaceholder}
          options={cityOptions}
          value={value ?? null}
          onChange={handleCityChange}
          error={cityError}
          disabled={disabled || isLoadingCities}
        />
      )}
    </div>
  );
}
